"use client";

/**
 * 관리자 로그인 페이지
 *
 * 이메일/PW 로그인 (카카오 미지원)
 */
import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { useAdminAuth } from "@/shared/auth/admin-provider";
import { validateFields } from "@/shared/form/validate";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const adminLoginSchema = z.object({
  email: z.string().email("올바른 이메일을 입력하세요"),
  password: z.string().min(1, "비밀번호를 입력하세요"),
});

type FieldErrors = Record<string, string[] | undefined>;

export default function AdminLoginPage() {
  return (
    <Suspense>
      <AdminLoginForm />
    </Suspense>
  );
}

function AdminLoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnUrl = searchParams.get("returnUrl") || "/admin";
  const { adminLogin } = useAdminAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fields, setFields] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  // Strip credentials from URL if present (prevents nginx/logger from persisting them)
  useEffect(() => {
    const url = new URL(window.location.href);
    const hasSecret = url.searchParams.has("password") || url.searchParams.has("pw");
    if (!hasSecret) return;
    const email = url.searchParams.get("email");
    if (email) setFields((f) => ({ ...f, email }));
    url.searchParams.delete("password");
    url.searchParams.delete("pw");
    url.searchParams.delete("email");
    window.history.replaceState(null, "", url.pathname + url.search);
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    const next = { ...fields, [name]: value };
    setFields(next);
    const { errors: errs } = validateFields(adminLoginSchema, next);
    setErrors(errs ?? {});
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const { data, errors: errs } = validateFields(adminLoginSchema, fields);
    if (errs) {
      setErrors(errs);
      return;
    }
    setIsSubmitting(true);
    try {
      await adminLogin(data.email, data.password);
      router.push(returnUrl);
    } catch (err) {
      const message = err instanceof Error ? err.message : "로그인에 실패했습니다";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_ADMIN_DATA_LOCAL === "true"
  ) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-xl font-semibold">로컬 데이터 관리 로그인</h1>
          <p className="text-sm text-muted-foreground">
            운영 데이터 복사본입니다. 조회와 변경은 로컬 DB에서만 실행합니다.
          </p>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full">
            <a href="/dev/admin-data-login">로컬 어드민으로 들어가기</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (
    process.env.NODE_ENV === "development" &&
    process.env.NEXT_PUBLIC_SETTLEMENT_LOCAL === "true"
  ) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-xl font-semibold">로컬 정산 검토 로그인</h1>
          <p className="text-sm text-muted-foreground">
            운영 데이터 복사본을 사용하는 검토 서버입니다. 운영 계정 비밀번호는 입력하지 않습니다.
          </p>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full">
            <a href="/dev/settlement-login">로컬 정산 화면으로 들어가기</a>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="items-center space-y-1 pb-2">
        <div className="flex items-center gap-2">
          <h1 className="font-heading text-2xl font-bold tracking-tight">dangil</h1>
          <span className="rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
            Admin
          </span>
        </div>
        <p className="text-sm text-muted-foreground">관리자 로그인</p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">이메일</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="admin@dangil.com"
              value={fields.email}
              onChange={handleChange}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email[0]}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">비밀번호</Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="비밀번호를 입력하세요"
                value={fields.password}
                onChange={handleChange}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-destructive">{errors.password[0]}</p>}
          </div>

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            로그인
          </Button>

          <div className="text-center">
            <Link
              href="/admin/forgot-password"
              className="text-sm text-muted-foreground transition-colors hover:text-primary"
            >
              비밀번호 찾기
            </Link>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
